import {
	Box,
	MenuItem,
	Select,
	SelectChangeEvent,
	SelectProps,
	styled,
	SvgIcon,
} from "@mui/material";
import { SurveyFieldType } from "@onair/repositories";
import { memo, useEffect, useId, useState } from "react";

const SURVEY_FIELD_TYPES: SurveyFieldType[] = [
	SurveyFieldType.TEXTAREA,
	SurveyFieldType.RADIO,
	SurveyFieldType.CHECKBOX,
	SurveyFieldType.SELECT,
	SurveyFieldType.RATING,
	// SurveyFieldType.NUMBER,
	// SurveyFieldType.MULTISELECT,
	// SurveyFieldType.DATE,
	// SurveyFieldType.TEXT,
];
export interface QuestionTypeSelectorProps {
	className?: string;
	onChange?: (value: SurveyFieldType) => void;
	value?: SurveyFieldType;
}
const CustomSelect = styled(Select)<SelectProps<SurveyFieldType>>(
	({ theme }) => ({
		width: 190,
		color: theme.palette.grey[900],
		"& .MuiSelect-select": {
			paddingTop: "0.75rem",
			paddingBottom: "0.75rem",
		},
		"& fieldset": {
			border: `none`,
			background: "rgb(145,158,171,0.1)",
		},
	}),
);
const QuestionTypeSelector: React.FC<QuestionTypeSelectorProps> = ({
	value = SURVEY_FIELD_TYPES[0],
	onChange,
}) => {
	const selectId = useId();
	const [selected, setSelected] = useState<SurveyFieldType>(value);

	const selectQuestionType = (event: SelectChangeEvent<unknown>) => {
		const newValue = event.target.value as SurveyFieldType;
		onChange?.(newValue);
		setSelected(newValue);
	};
	useEffect(() => {
		setSelected(value);
	}, [value]);
	return (
		<CustomSelect id={selectId} value={selected} onChange={selectQuestionType}>
			{SURVEY_FIELD_TYPES.map((item) => (
				<MenuItem value={item} key={item} className="py-3">
					{renderOption(item)}
				</MenuItem>
			))}
		</CustomSelect>
	);
};
export default memo(QuestionTypeSelector);

const renderOption = (key: SurveyFieldType) => {
	if (key === SurveyFieldType.TEXTAREA) {
		return (
			<Box component="div" className="flex gap-x-2">
				<SvgIcon
					width="24"
					height="25"
					viewBox="0 0 24 25"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						opacity="0.32"
						d="M15.2798 5.31348H4.7202C3.77169 5.31348 3 5.87404 3 6.5639C3 7.25291 3.77169 7.81348 4.7202 7.81348H15.2798C16.2283 7.81348 17 7.25291 17 6.5639C17 5.87402 16.2283 5.31348 15.2798 5.31348Z"
						fill="#1877F2"
					/>
					<path
						d="M19.2798 11.5635H8.7202C7.77169 11.5635 7 12.124 7 12.8139C7 13.5029 7.77169 14.0635 8.7202 14.0635H19.2798C20.2283 14.0635 21 13.5029 21 12.8139C21 12.124 20.2283 11.5635 19.2798 11.5635Z"
						fill="#1877F2"
					/>
					<path
						d="M15.2798 17.8135H4.7202C3.77169 17.8135 3 18.374 3 19.0639C3 19.7529 3.77169 20.3135 4.7202 20.3135H15.2798C16.2283 20.3135 17 19.7529 17 19.0639C17 18.374 16.2283 17.8135 15.2798 17.8135Z"
						fill="#1877F2"
					/>
				</SvgIcon>
				<span>Câu trả lời</span>
			</Box>
		);
	}

	if (key === SurveyFieldType.RADIO) {
		return (
			<Box component="div" className="flex gap-x-2">
				<SvgIcon
					width="24"
					height="25"
					viewBox="0 0 24 25"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M12 18.8135C8.68629 18.8135 6 16.1272 6 12.8135C6 9.49977 8.68629 6.81348 12 6.81348C15.3137 6.81348 18 9.49977 18 12.8135C18 16.1272 15.3137 18.8135 12 18.8135Z"
						fill="#0050FF"
					/>
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M2 12.8135C2 18.3363 6.47715 22.8135 12 22.8135C17.5228 22.8135 22 18.3363 22 12.8135C22 7.29063 17.5228 2.81348 12 2.81348C6.47715 2.81348 2 7.29063 2 12.8135ZM3.5 12.8135C3.5 8.11906 7.30558 4.31348 12 4.31348C16.6944 4.31348 20.5 8.11906 20.5 12.8135C20.5 17.5079 16.6944 21.3135 12 21.3135C7.30558 21.3135 3.5 17.5079 3.5 12.8135Z"
						fill="#0050FF"
					/>
				</SvgIcon>
				<span>Một câu trả lời</span>
			</Box>
		);
	}
	if (key === SurveyFieldType.CHECKBOX) {
		return (
			<Box component="div" className="flex gap-x-2">
				<SvgIcon
					width="24"
					height="25"
					viewBox="0 0 24 25"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<rect x="6" y="6.81348" width="12" height="12" fill="white" />
					<path
						d="M17 2.81348C19.7614 2.81348 22 5.05205 22 7.81348V17.8135C22 20.5749 19.7614 22.8135 17 22.8135H7C4.23858 22.8135 2 20.5749 2 17.8135V7.81348C2 5.05205 4.23858 2.81348 7 2.81348H17ZM16.6152 10.0684C16.2753 9.72838 15.715 9.72842 15.375 10.0684L11.2451 14.1982L9.49512 12.4482C9.15513 12.1083 8.59487 12.1083 8.25488 12.4482C7.9149 12.7882 7.91494 13.3385 8.25488 13.6885L10.6348 16.0586C10.8048 16.2286 11.0251 16.3086 11.2451 16.3086C11.4751 16.3086 11.6953 16.2286 11.8652 16.0586L16.6152 11.3086C16.9552 10.9686 16.9552 10.4183 16.6152 10.0684Z"
						fill="#0050FF"
					/>
				</SvgIcon>
				<span>Nhiều câu trả lời</span>
			</Box>
		);
	}

	if (key === SurveyFieldType.RATING) {
		return (
			<Box component="div" className="flex gap-x-2">
				<SvgIcon
					width="24"
					height="25"
					viewBox="0 0 24 25"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						opacity="0.32"
						fillRule="evenodd"
						clipRule="evenodd"
						d="M1.5 12.8135C1.5 18.6125 6.20101 23.3135 12 23.3135C17.799 23.3135 22.5 18.6125 22.5 12.8135C22.5 7.01449 17.799 2.31348 12 2.31348C6.20101 2.31348 1.5 7.01449 1.5 12.8135ZM12.9491 6.62073C12.5501 5.87773 11.4499 5.87773 11.051 6.62073L9.54862 9.41883L6.36352 10.0247C5.54932 10.1795 5.22022 11.1325 5.77507 11.7286L8.03362 14.155L7.61877 17.4023C7.51432 18.2199 8.39427 18.8208 9.15727 18.4529L12 17.0822L14.8428 18.4529C15.6058 18.8208 16.4857 18.2199 16.3813 17.4023L15.9664 14.155L18.225 11.7286C18.7798 11.1325 18.4507 10.1795 17.6365 10.0247L14.4514 9.41883L12.9491 6.62073Z"
						fill="#0050FF"
					/>
					<path
						d="M11.051 6.62073C11.4499 5.87773 12.5501 5.87773 12.9491 6.62073L14.4514 9.41883L17.6365 10.0247C18.4507 10.1795 18.7798 11.1325 18.225 11.7286L15.9664 14.155L16.3813 17.4023C16.4857 18.2199 15.6058 18.8208 14.8428 18.4529L12 17.0822L9.15727 18.4529C8.39427 18.8208 7.51432 18.2199 7.61877 17.4023L8.03362 14.155L5.77507 11.7286C5.22022 11.1325 5.54932 10.1795 6.36352 10.0247L9.54862 9.41883L11.051 6.62073Z"
						fill="#0050FF"
					/>
				</SvgIcon>
				<span>Xếp hạng</span>
			</Box>
		);
	}
	if (key === SurveyFieldType.SELECT) {
		return (
			<Box component="div" className="flex gap-x-2">
				<SvgIcon
					width="24"
					height="25"
					viewBox="0 0 24 25"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						opacity="0.5"
						d="M5.00056 7.06348C4.84719 7.06336 4.69748 7.11027 4.5716 7.19787C4.44572 7.28547 4.34971 7.40956 4.29653 7.55341C4.24335 7.69726 4.23555 7.85395 4.27418 8.00237C4.3128 8.15079 4.396 8.2838 4.51256 8.38348L11.5126 14.3835C11.7926 14.6235 12.2076 14.6235 12.4886 14.3835L19.4886 8.38348C19.6051 8.2838 19.6883 8.15079 19.7269 8.00237C19.7656 7.85395 19.7578 7.69726 19.7046 7.55341C19.6514 7.40956 19.5554 7.28547 19.4295 7.19787C19.3036 7.11027 19.1539 7.06336 19.0006 7.06348H5.00056Z"
						fill="#0050FF"
					/>
					<path
						fillRule="evenodd"
						clipRule="evenodd"
						d="M4.42993 11.3253C4.49406 11.2505 4.57231 11.1891 4.6602 11.1445C4.74808 11.0999 4.84389 11.0731 4.94215 11.0656C5.04041 11.0581 5.13918 11.07 5.23283 11.1007C5.32648 11.1313 5.41316 11.1802 5.48793 11.2443L11.9999 16.8253L18.5119 11.2443C18.5866 11.1795 18.6734 11.13 18.7673 11.0988C18.8611 11.0676 18.9602 11.0553 19.0589 11.0626C19.1575 11.0698 19.2538 11.0966 19.3421 11.1412C19.4303 11.1858 19.5089 11.2474 19.5733 11.3225C19.6377 11.3976 19.6865 11.4848 19.7171 11.5788C19.7477 11.6729 19.7593 11.7721 19.7514 11.8707C19.7434 11.9693 19.7161 12.0654 19.6709 12.1533C19.6257 12.2413 19.5635 12.3195 19.4879 12.3833L12.4879 18.3833C12.352 18.4998 12.1789 18.5638 11.9999 18.5638C11.8209 18.5638 11.6479 18.4998 11.5119 18.3833L4.51193 12.3833C4.43711 12.3192 4.37566 12.241 4.33109 12.1531C4.28652 12.0652 4.25971 11.9694 4.25219 11.8711C4.24467 11.7729 4.25658 11.6741 4.28725 11.5805C4.31792 11.4868 4.36675 11.4001 4.43094 11.3253H4.42993Z"
						fill="#0050FF"
					/>
				</SvgIcon>
				<span>Menu thả xuống</span>
			</Box>
		);
	}
};
